import { useEffect, useState } from "react";
import s from "../wizard.module.css";
import {
  Section,
  Field,
  TextField,
  TextAreaField,
  CedulaField,
  TipoPersonaSwitch,
  VehiculosField,
} from "../FieldHelpers";
import { useCedulaLookup } from "../../services/useCedulaLookup";
import { validateCopropietario } from "../../services/validationRules";
import { overlayClose } from "../../../../shared/utils/overlayClose";

const EMPTY = {
  id: null,
  cedula: "",
  nombre: "",
  apellidos: "",
  es_sociedad: false,
  nombre_sociedad: "",
  telefono_casa: "",
  telefono_cel: "",
  telefono_trabajo: "",
  vehiculos: [],
  notas: "",
};

export default function ModalCopropietario({
  open,
  onClose,
  formData,
  setFormData,
  mode,
  selectedIndex,
}) {
  const [coOwner, setCoOwner] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const { buscar, resultado, estado, reset } = useCedulaLookup();

  useEffect(() => {
    if (mode === "edit" && selectedIndex !== null) {
      const selected = formData.copropietarios.find((c) => c.id === selectedIndex);
      setCoOwner(selected ? { ...EMPTY, ...selected } : EMPTY);
    } else {
      setCoOwner(EMPTY);
    }
    setErrors({});
    reset();
    // formData.copropietarios y reset se omiten a propósito: reset se recrea
    // en cada render (no memoizado en useCedulaLookup) y agregarlo dispararía
    // este efecto en cada render, no solo al abrir el modal o cambiar de selección.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, selectedIndex]);

  // Datos del registro: si la cédula es de una sociedad, pasa el switch a Sociedad
  useEffect(() => {
    if (resultado) {
      setCoOwner((prev) => ({
        ...prev,
        nombre: resultado.nombre || prev.nombre,
        apellidos: resultado.apellidos || prev.apellidos,
        ...(resultado.nombre_sociedad
          ? { es_sociedad: true, nombre_sociedad: resultado.nombre_sociedad }
          : {}),
      }));
    }
  }, [resultado]);

  if (!open) return null;

  const handleChange = (e) => setCoOwner((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  function handleSave(e) {
    e.preventDefault(); // evitar que el form del asistente haga submit
    e.stopPropagation();
    const errs = validateCopropietario(coOwner);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    if (mode === "add") {
      setFormData((prev) => ({
        ...prev,
        copropietarios: [...prev.copropietarios, { ...coOwner, id: Date.now() }],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        copropietarios: prev.copropietarios.map((c) =>
          c.id === selectedIndex ? { ...coOwner, id: selectedIndex } : c,
        ),
      }));
    }
    onClose();
  }

  const esEdicion = mode === "edit";

  return (
    <div className={`${s.wizard} ${s.overlay}`} {...overlayClose(onClose)}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalAccent} />

        <div className={s.modalHeader}>
          <div>
            <h2 className={s.modalTitle}>
              {esEdicion ? "Editar Copropietario" : "Agregar Copropietario"}
            </h2>
            <p className={s.modalSub}>
              Complete los datos del co-titular para asociarlo formalmente a esta filial con
              derecho de acceso.
            </p>
          </div>
          <button type="button" className={s.btnClose} onClick={onClose} title="Cerrar">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className={s.modalBody}>
          {Object.keys(errors).length > 0 && (
            <div className={s.alert}>
              <i className="fa-solid fa-triangle-exclamation" />
              Revisá los campos marcados antes de guardar.
            </div>
          )}

          <Section title="Datos de Identificación">
            <Field label="Tipo de Persona">
              <TipoPersonaSwitch
                ancho
                value={coOwner.es_sociedad}
                onChange={(v) => setCoOwner((prev) => ({ ...prev, es_sociedad: v }))}
                labels={["Persona física", "Sociedad / Jurídica"]}
              />
            </Field>

            <CedulaField
              label="Cédula de Identidad / DIMEX / NITE"
              value={coOwner.cedula}
              onChange={handleChange}
              onBlur={() => buscar(coOwner.cedula)}
              onBuscar={() => buscar(coOwner.cedula)}
              estado={estado}
              error={errors.cedula}
            />

            {coOwner.es_sociedad ? (
              <TextField
                name="nombre_sociedad"
                label="Nombre de la sociedad"
                required
                placeholder="Ej: Inversiones Sol S.A."
                value={coOwner.nombre_sociedad || ""}
                onChange={handleChange}
                error={errors.nombre_sociedad}
              />
            ) : (
              <div className={s.grid2}>
                <TextField
                  name="nombre"
                  label="Nombre completo"
                  required
                  placeholder="Nombre"
                  value={coOwner.nombre}
                  onChange={handleChange}
                  error={errors.nombre}
                />
                <TextField
                  name="apellidos"
                  label="Apellidos"
                  required
                  placeholder="Apellidos"
                  value={coOwner.apellidos}
                  onChange={handleChange}
                  error={errors.apellidos}
                />
              </div>
            )}
          </Section>

          <Section title="Canales de Contacto">
            <div className={s.grid3}>
              <TextField
                name="telefono_cel"
                label="Teléfono Celular"
                optional
                placeholder="Ej: 8888-1234"
                value={coOwner.telefono_cel || ""}
                onChange={handleChange}
                error={errors.telefono_cel}
              />
              <TextField
                name="telefono_casa"
                label="Teléfono Casa"
                optional
                placeholder="Ej: 2222-1234"
                value={coOwner.telefono_casa || ""}
                onChange={handleChange}
                error={errors.telefono_casa}
              />
              <TextField
                name="telefono_trabajo"
                label="Teléfono Trabajo"
                optional
                placeholder="Ej: 2200-1234"
                value={coOwner.telefono_trabajo || ""}
                onChange={handleChange}
                error={errors.telefono_trabajo}
              />
            </div>
          </Section>

          <div className={s.grid2}>
            <Section title="Vehículos Asociados" subtitle="Placas con las que puede entrar. (opcional)">
              <VehiculosField
                variant="bandeja"
                vehiculos={coOwner.vehiculos || []}
                errors={errors.vehiculos}
                onChange={(vehiculos) => setCoOwner((prev) => ({ ...prev, vehiculos }))}
                textoAgregar={["Agregar placa", "Agregar placa adicional"]}
              />
            </Section>

            <Section title="Notas y Observaciones" subtitle="Parentesco, porcentaje o restricciones. (opcional)">
              <TextAreaField
                name="notas"
                rows={4}
                placeholder="Observaciones opcionales: parentesco, copropiedad porcentual o restricciones de acceso…"
                value={coOwner.notas || ""}
                onChange={handleChange}
              />
            </Section>
          </div>
        </div>

        <div className={s.modalFooter}>
          <button type="button" className={s.btnGhost} onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className={s.btnPrimary} onClick={handleSave}>
            <i className="fa-solid fa-check" />
            <span className={s.labelLong}>
              {esEdicion ? "Guardar Cambios" : "Guardar Copropietario"}
            </span>
            <span className={s.labelShort}>Guardar</span>
          </button>
        </div>
      </div>
    </div>
  );
}
